/**
 * THE BLOCKING SCREEN (ruling R-0919-2).
 *
 * A POSTER, NOT A MODAL, and that is the design decision rather than a styling
 * one. A modal is a thing on top of the product, which invites a person to
 * look past it and invites a developer to give it a close button; this is the
 * product, for as long as the server says a version is unaccepted. It is drawn
 * by the root layout in place of the router's outlet, so there is nothing
 * behind it and no navigation that works.
 *
 * THE CLIENT DECIDES NOTHING. Every word on it came from `/me`: which
 * documents are pending, at which version, the rollout's own "what changed"
 * prose, and the consent sentence whose hash the accept carries. There is no
 * version comparison here, no date arithmetic and no opinion about which
 * change mattered — all of that is the server's, and a second implementation
 * in a bundle is a second thing to disagree.
 *
 * ONE BUTTON FOR BOTH DOCUMENTS, two accepts on the wire. Splitting the press
 * would ask somebody to agree to half a change; splitting the REQUEST would
 * mean a route that takes two documents, which is a second shape for a thing
 * that already has one. So the press sends one accept per pending document and
 * only re-reads when all of them have answered.
 *
 * AND A WAY OUT THAT IS NOT ACCEPTING. "I do not accept" has to be an answer
 * somebody can give, so Sign out is on the poster and the server's gate lets
 * it through. The two documents open in new tabs — leaving the poster to read
 * the thing it is asking about would be a trap.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { API_URL } from "../../lib/config";
import { acceptLegal, type Me } from "../../lib/api";
import { useLeave } from "../ui/account";
import { qk } from "../../lib/queries";
import { OnboardFace, OnboardShell } from "../onboarding/steps";

export const ACCEPT_TITLE = "Something changed";
export const ACCEPT_LEAD =
  "Nob's legal text has been updated. Have a read, and carry on when you're ready.";
export const ACCEPT_LABEL = "I accept";
export const ACCEPT_BUSY_LABEL = "Recording…";
export const SIGN_OUT_LABEL = "Sign out instead";
/**
 * A refusal a person can act on, and there is only one thing to do about any
 * of them: the screen re-reads `/me`, and either the poster goes or it comes
 * back with what the server says now.
 */
export const ACCEPT_REFUSED = "That did not go through. Try again in a moment.";

const TITLE: Record<"terms" | "privacy", string> = {
  terms: "Terms of use",
  privacy: "Privacy policy",
};

export function LegalAcceptPoster({ legal }: { legal: Me["legal"] }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The one exit in the product, not a second implementation of it: signing
  // out ends a person and clears three local stores, and the gate lets it
  // through precisely so this can be here.
  const { leave, leaving } = useLeave();
  const summaries = [...new Set(legal.pending.map((p) => p.summary).filter(Boolean))];

  const accept = async () => {
    if (busy || legal.sentence.length === 0) return;
    setBusy(true);
    setError(null);
    // Sequential rather than parallel: each write is idempotent and the whole
    // press is one act, so a partial success is a state the next `/me` reads
    // correctly anyway. Two requests are two requests, not a transaction.
    let refused = false;
    for (const pending of legal.pending) {
      const r = await acceptLegal(pending.document, pending.version, legal.sentence);
      if (!r.ok) refused = true;
    }
    /**
     * NOTHING IS TRACKED HERE, and that is deliberate rather than an
     * omission. Privacy 2.4 §3.2 promises a CLOSED set of analytics events and
     * then lists it; a twelfth emitted from this screen would be a collection
     * nobody disclosed. The acceptance is already recorded where it belongs —
     * in the ledger, by the server.
     */
    // Re-read either way. A refusal is usually a screen that has drifted from
    // the server, and the cure for that is the server's own answer.
    await queryClient.invalidateQueries({ queryKey: qk.me() });
    setBusy(false);
    if (refused) setError(ACCEPT_REFUSED);
  };

  return (
    <OnboardShell>
      <OnboardFace mood="doubting" />

      <h1 className="nob-poster-title">{ACCEPT_TITLE}</h1>
      <p className="nob-poster-lead">{ACCEPT_LEAD}</p>

      <div className="mt-6 w-full space-y-4">
        {legal.pending.map((pending) => (
          <p key={pending.document} className="nob-poster-footnote">
            <a
              href={`${API_URL}/${pending.document === "terms" ? "terms" : "privacy"}`}
              target="_blank"
              rel="noreferrer"
              className="nob-poster-inline"
            >
              {TITLE[pending.document]}
            </a>{" "}
            · version {pending.version}
          </p>
        ))}
        {/* The rollouts' own words, and DISTINCT ones only. The two documents
            are usually revised together and published with one sentence
            between them; printing it twice would read as two changes. */}
        {summaries.map((summary) => (
          <p key={summary} className="nob-poster-lead">
            {summary}
          </p>
        ))}
      </div>

      <div className="nob-poster-cta">
        <button
          type="button"
          className="nob-poster-btn"
          disabled={busy || legal.sentence.length === 0 || legal.pending.length === 0}
          onClick={() => void accept()}
        >
          {busy ? ACCEPT_BUSY_LABEL : ACCEPT_LABEL}
        </button>
        {error && (
          <p className="nob-poster-error" role="alert">
            {error}
          </p>
        )}
      </div>

      {/* SMALL PRINT UNDER THE BUTTON IT DESCRIBES, and the hash of these exact
          bytes is what the accept sends — the same discipline the age
          attestation follows, and the same reason: a sentence this side
          composed is one nobody could prove was shown. */}
      <p className="nob-poster-legal">{legal.sentence}</p>

      <p className="nob-poster-footnote">
        <button type="button" className="nob-poster-inline" disabled={leaving} onClick={leave}>
          {SIGN_OUT_LABEL}
        </button>
      </p>
    </OnboardShell>
  );
}
